import {
  Button,
  Checkbox,
  createUserDataLabel,
  useLocalStorage,
} from "@hoylu/client-common";
import Scrollbar from "@hoylu/client-common/dist/assets/css.modules/scrollbar.module.css";
import { clsx } from "clsx";
import { chain, isEmpty } from "lodash";
import React, {
  type ButtonHTMLAttributes,
  type DetailedHTMLProps,
  Fragment,
  type InputHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Dictionary, MarkRequired, Primitive } from "ts-essentials";
import { selectShareLink } from "../../../selectors/selectShareLink.selector";
import type { AppState } from "../../../state/root.reducer";
import { createSelectProjectByIdFillingUnknownOne } from "../../../state/workspaces/projects.selector";
import type { WorkspaceRole } from "../../../state/workspaces/types";
import {
  inviteToWorkspace,
  upsertWorkspaceUsers,
} from "../../../state/workspaces/workspaces.actions";
import {
  selectIsUpdatingWorkspace,
  selectPublicPermissionLevel,
  selectSelectedWorkspace,
  selectSelectedWorkspaceRoles,
} from "../../../state/workspaces/workspaces.selector";
import { Localized } from "../../../strings";
import { emailRegex } from "../../../utils/emailRegex";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { managementPortalUserProfileUrl as selectManagementPortalUserProfileUrl } from "../../../utils/management.portal/management.portal.url.selectors";
import type { RoleUserPermission } from "../../../utils/sharing.helpers";
import {
  getShareOptionFromWorkspaceRole,
  getWorkspaceRoleFromShareOption,
  getWorkspaceRoles,
  sendEmail,
} from "../../../utils/sharing.helpers";
import type { Contact, FetchProps } from "../../RecipientInput";
import { makeContact, RecipientInput } from "../../RecipientInput";
import { PermissionsBar } from "./PermissionsBar";
import { ShareType } from "./ShareType";
import { UserPermissionsBar } from "./UserPermissionsBar";
import Styles from "./WorkspacePermissions.module.css";

const RECENT_EMAILS = "__recent_emails";

type Field = "email" | "name" | "permission";

type SortOrder = "asc" | "desc" | undefined;

interface OrderByState {
  field: Field;
  order: SortOrder;
}

type FieldAccessor<TData> = (data: TData) => Primitive;

type OrderByArgs<TField extends string, TData> = Dictionary<
  {
    accessor: FieldAccessor<TData>;
    orderBy: Dictionary<NonNullable<SortOrder>, NonNullable<SortOrder>>;
  },
  TField
>;

const orderByArgs: OrderByArgs<Field, RoleUserPermission> = {
  email: {
    accessor: (userData) => userData.email?.toLowerCase(),
    orderBy: { asc: "asc", desc: "desc" },
  },
  name: {
    accessor: (userData) =>
      userData.email && createUserDataLabel(userData).toLowerCase(),
    orderBy: { asc: "asc", desc: "desc" },
  },
  permission: {
    accessor: (userData) =>
      getShareOptionFromWorkspaceRole(userData.currentRole).value,
    orderBy: { asc: "desc", desc: "asc" },
  },
};

const filterBy = (searchTerm: string) => {
  const lowerCasedSearchTerm = searchTerm.toLowerCase();
  return (data: RoleUserPermission) =>
    createUserDataLabel(data).toLowerCase().includes(lowerCasedSearchTerm);
};

const getOrderByArgs = ({
  field,
  order = "asc",
}: OrderByState): [
  FieldAccessor<RoleUserPermission>,
  [NonNullable<SortOrder>],
] => {
  const { accessor, orderBy } = orderByArgs[field];
  return [accessor, [orderBy[order]]];
};

const getNextSortOrder = (order: SortOrder): SortOrder => {
  switch (order) {
    case "asc":
      return "desc";
    case "desc":
      return undefined;
    case undefined:
      return "asc";
  }
};

const userAccessOptions: ShareOptionValue[] = [
  ShareType.ADMIN,
  ShareType.EDIT,
  ShareType.READ,
  ShareType.NO_ACCESS,
];

type WorkspacePermissionsProps = {
  isAddingUsersFromExternalSource: boolean;
  selectedContacts: Contact[];
  setSelectedContacts: (contacts: Contact[]) => void;
};

const selectGetProjectByIdFillingUnknownOne =
  createSelectProjectByIdFillingUnknownOne();

export const WorkspacePermissions: React.FC<WorkspacePermissionsProps> = ({
  isAddingUsersFromExternalSource,
  selectedContacts,
  setSelectedContacts,
}) => {
  const dispatch = useDispatch();

  const tShareType = useI18n("SHARE_TYPE.");
  const tWorkspacePerms = useI18n("WORKSPACE_PERMISSIONS.");

  const selectedMyWorkspaceMetadata = useSelector(selectSelectedWorkspace);
  const selectedWorkspaceRoles = useSelector(selectSelectedWorkspaceRoles);
  const email = useSelector(
    (state: AppState) => state.context.user.profile.email,
  );
  const isUpdatingWorkspace = useSelector(selectIsUpdatingWorkspace);
  const publicPermissionLevel = useSelector(selectPublicPermissionLevel);
  const showOrgAdminPolicy = useSelector((state: AppState) => {
    return !!(
      state.context.user.policies?.allOrgAdminsHaveAdminAccess &&
      selectedMyWorkspaceMetadata?.orgId === state.context.user.profile.orgId
    );
  });
  const shareLink = useSelector((state: AppState) =>
    selectShareLink(state, selectedMyWorkspaceMetadata?.workspaceId),
  );
  const project = useSelector((state: AppState) =>
    selectGetProjectByIdFillingUnknownOne(
      state,
      selectedMyWorkspaceMetadata?.containerId,
    ),
  );
  const managementPortalUserProfileUrl = useSelector(
    selectManagementPortalUserProfileUrl,
  );

  const [isAddingUsers, setIsAddingUsers] = useState(
    isAddingUsersFromExternalSource,
  );
  const [orderBy, setOrderBy] = useState<OrderByState>({
    field: "email",
    order: "asc",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [newUsersPermission, setNewUsersPermission] = useState(ShareType.EDIT);
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [requestedEmails, setRequestedEmails] = useState<string[]>([]);

  const [recentEmails, setRecentEmails] = useLocalStorage<string[]>(
    RECENT_EMAILS,
    [],
  );

  const recentContacts = useMemo(() => {
    const counts = recentEmails.reduce<Record<string, number>>((map, val) => {
      map[val] = (map[val] || 0) + 1;
      return map;
    }, {});
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([v]) => makeContact(v));
  }, [recentEmails]);

  const fetchContactsLocal = (value: string, props?: FetchProps) => {
    return fetchContacts(value, { ...props, allContacts: recentContacts });
  };

  const isAdmin = !!selectedMyWorkspaceMetadata?.myPermissions.canAdmin;

  const { userEmails, users } = useMemo(() => {
    const users = chain(getWorkspaceRoles(selectedWorkspaceRoles))
      .filter(filterBy(searchTerm))
      .orderBy(...getOrderByArgs(orderBy))
      .value();
    return {
      users,
      userEmails: new Set(users.map((u) => u.email.toLowerCase())),
    };
  }, [orderBy, searchTerm, selectedWorkspaceRoles]);

  const emailsWithoutAccount = useMemo(
    () => requestedEmails.filter((re) => !userEmails.has(re.toLowerCase())),
    [requestedEmails, userEmails],
  );

  useEffect(() => {
    if (!isEmpty(requestedEmails) && isEmpty(emailsWithoutAccount)) {
      setRequestedEmails([]);
    }
  }, [emailsWithoutAccount, requestedEmails]);

  const clearRequestedEmails = () => setRequestedEmails([]);

  const upsertUsers = useCallback(
    (userEmails: string[], permission: WorkspaceRole) => {
      if (!selectedMyWorkspaceMetadata) return;

      clearRequestedEmails();
      dispatch(
        upsertWorkspaceUsers.request({
          workspaceId: selectedMyWorkspaceMetadata.workspaceId,
          userEmails,
          permission,
        }),
      );
    },
    [selectedMyWorkspaceMetadata?.workspaceId, dispatch, clearRequestedEmails],
  );

  const evaluateEmailList = useCallback(() => {
    const userIsNotInWorkspace = (userEmail: string) =>
      !users.some((u) => u.email === userEmail);

    const emailList = selectedContacts
      .map((e) => e.email.toLowerCase())
      .filter((e) => e.match(emailRegex)) // filter out invalid emails
      .filter(userIsNotInWorkspace); // filter out existing emails, because adding new users should not overwrite the permission of a user already in the workspace

    const index = emailList.indexOf(email.trim());
    if (index > -1) {
      emailList.splice(index, 1);
    }
    if (emailList.length !== 0) {
      setRecentEmails((re) => [...emailList, ...re].slice(0, 100));
      upsertUsers(
        emailList,
        getWorkspaceRoleFromShareOption(newUsersPermission),
      );
      setRequestedEmails(emailList);
      if (notifyUsers) {
        sendEmail(
          selectedMyWorkspaceMetadata?.workspaceName ?? "",
          shareLink,
          emailList,
        );
        setNotifyUsers(false);
      }
    }
    setIsAddingUsers(false);
    setSelectedContacts([]);
  }, [
    newUsersPermission,
    notifyUsers,
    selectedContacts,
    selectedMyWorkspaceMetadata?.workspaceName,
    shareLink,
    upsertUsers,
    users,
  ]);

  const handleSortBy = useCallback(
    (field: Field) => () => {
      setOrderBy((orderBy) => {
        const order = getNextSortOrder(
          orderBy.field === field ? orderBy.order : undefined,
        );
        return {
          field: order ? field : "email",
          order,
        };
      });
    },
    [],
  );

  const getTitle = (
    field: Field,
    fieldKey: string,
    fallback = "FIELDS.EMAIL",
  ) =>
    orderBy.field === field
      ? orderBy.order === "asc" // NOSONAR
        ? tWorkspacePerms("SORT.BY_X_DSC", tWorkspacePerms(fieldKey))
        : tWorkspacePerms("SORT.BY_X", tWorkspacePerms(fallback))
      : tWorkspacePerms("SORT.BY_X_ASC", tWorkspacePerms(fieldKey));

  return (
    <div className={Styles.workspacePermissions}>
      {!isAddingUsers ? (
        <Fragment>
          <div className={Styles.permissionsContainer}>
            <div className={clsx(Styles.permissionsHeaderRow)}>
              <div className={clsx(Styles.sortButtons)}>
                <HeaderButton
                  id="name"
                  onClick={handleSortBy("name")}
                  orderBy={orderBy}
                  title={getTitle("name", "FIELDS.NAME")}
                >
                  {tWorkspacePerms("FIELDS.NAME")}
                </HeaderButton>
                <HeaderButton
                  id="permission"
                  onClick={handleSortBy("permission")}
                  orderBy={orderBy}
                  title={getTitle("permission", "FIELDS.PERMISSION")}
                >
                  {tWorkspacePerms("FIELDS.PERMISSION")}
                </HeaderButton>
              </div>
              <HeaderSearch
                id="permissions-search"
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm("")}
                value={searchTerm}
              />
            </div>
            <div className={clsx(Styles.usersPermissions, Scrollbar.default)}>
              {selectedMyWorkspaceMetadata &&
                users.map((user) => (
                  <UserPermissionsBar
                    isAdmin={isAdmin}
                    key={user.userId}
                    options={userAccessOptions}
                    removable={
                      isAdmin &&
                      email.toLowerCase().trim() !==
                        user.email.toLowerCase().trim()
                    }
                    user={user}
                    workspace={selectedMyWorkspaceMetadata}
                  />
                ))}
              {showOrgAdminPolicy && (
                <PermissionsBar
                  label={tWorkspacePerms("BY_POLICY_ANY_ORG_ADMIN")}
                  options={userAccessOptions.map((option) => ({
                    ...option,
                    disabled: true,
                    inactive: true,
                    selected: option.value === ShareType.ADMIN.value,
                    title: tShareType(option.title),
                    tooltip: option.tooltip && tShareType(option.tooltip),
                  }))}
                />
              )}
            </div>
          </div>
          {!isUpdatingWorkspace && emailsWithoutAccount.length > 0 && (
            <EmailsWithoutAccount
              workspaceId={selectedMyWorkspaceMetadata?.workspaceId ?? ""}
              {...{
                clearRequestedEmails,
                emailsWithoutAccount,
                newUsersPermission,
              }}
            />
          )}
          <div className={Styles.importUsers}>
            {isAdmin && (
              <Button
                onClick={() => {
                  if (project) {
                    if (project.hasAdmin && managementPortalUserProfileUrl) {
                      window.open(
                        `${managementPortalUserProfileUrl}/projects/${project.id}`,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }
                  } else {
                    clearRequestedEmails();
                    setIsAddingUsers(true);
                  }
                }}
                type="primary"
              >
                {tWorkspacePerms(
                  project ? "ADD_USERS_TO_PROJECT" : "ADD_USERS",
                )}
              </Button>
            )}
          </div>
        </Fragment>
      ) : (
        <div className={Styles.addUsers}>
          <span className={Styles.addUsersTitle}>
            {tWorkspacePerms("ADD_USERS")}
          </span>
          <PermissionsBar
            label={tWorkspacePerms("USER_PERMISSION")}
            options={userAccessOptions.map((option) => {
              const effectiveValue =
                publicPermissionLevel.value > newUsersPermission.value
                  ? publicPermissionLevel.value
                  : newUsersPermission.value;
              return {
                ...option,
                disabled: !isAdmin || option.value === newUsersPermission.value,
                highlight: option.value === newUsersPermission.value,
                onClick: () => setNewUsersPermission(option),
                selected: option.value === effectiveValue,
                title: tShareType(option.title),
                tooltip: option.tooltip && tShareType(option.tooltip),
              };
            })}
          />
          <RecipientInput
            fetchContacts={fetchContactsLocal}
            onChange={(selection) => setSelectedContacts(selection)}
            selectedContacts={selectedContacts}
            strings={Localized.object("WORKSPACE_PERMISSIONS")}
          />
          <div className={Styles.addUsersFooter}>
            <div className={Styles.notifyUsers}>
              <Checkbox
                checked={notifyUsers}
                onChange={() => {
                  setNotifyUsers(!notifyUsers);
                }}
              >
                <span>{tWorkspacePerms("NOTIFY_USERS")}</span>
              </Checkbox>
            </div>
            <div className={Styles.buttons}>
              <Button
                onClick={() => {
                  setIsAddingUsers(false);
                  setSelectedContacts([]);
                }}
                type="negative"
              >
                {tWorkspacePerms("CANCEL")}
              </Button>
              <Button
                classNames={
                  newUsersPermission.color
                    ? [newUsersPermission.color]
                    : undefined
                }
                onClick={evaluateEmailList}
                type="primary"
              >
                {tWorkspacePerms(
                  "ADD_WITH_ACCESS",
                  tShareType(newUsersPermission.title),
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type EmailsWithoutAccountProps = {
  clearRequestedEmails: () => void;
  emailsWithoutAccount: string[];
  newUsersPermission: ShareOptionValue;
  workspaceId: string;
};

const EmailsWithoutAccount: React.FC<EmailsWithoutAccountProps> = ({
  clearRequestedEmails,
  emailsWithoutAccount,
  newUsersPermission,
  workspaceId,
}) => {
  const dispatch = useDispatch();
  const t = useI18n("WORKSPACE_PERMISSIONS.");

  const sendInvite = () => {
    dispatch(
      inviteToWorkspace.request({
        invite: {
          emails: emailsWithoutAccount,
          reason: "Invite to join Hoylu",
        },
        workspaceId,
        newUsersPermission: getWorkspaceRoleFromShareOption(newUsersPermission),
      }),
    );
    clearRequestedEmails();
  };

  return (
    <>
      <div>{t("NO_ACCOUNTS_ERROR")}</div>
      <ul>
        {emailsWithoutAccount.map((email) => (
          <li key={email}>{email}</li>
        ))}
      </ul>
      <div>
        <Button onClick={sendInvite} toolTip={t("INVITE_AND_ADD")}>
          {t("INVITE_AND_ADD")}
        </Button>
        <Button onClick={clearRequestedEmails} toolTip={t("CANCEL")}>
          {t("CANCEL")}
        </Button>
      </div>
    </>
  );
};

function getContacts(
  searchValue: string,
  { omitContacts = [], limit, allContacts = [] }: FetchProps = {},
) {
  const remainingContacts = allContacts.filter(
    (c) => !omitContacts.some((sc) => sc && sc.id === c.id),
  );
  const sortedContacts = searchValue
    ? remainingContacts.filter((c) => c.name.includes(searchValue))
    : remainingContacts;
  const limitedContacts = limit
    ? sortedContacts.slice(0, limit)
    : sortedContacts;
  return limitedContacts;
}

function fetchContacts(
  searchValue: string,
  { omitContacts, limit, requestId, allContacts }: FetchProps = {},
) {
  return Promise.resolve({
    response: {
      data: getContacts(searchValue, { omitContacts, limit, allContacts }),
      requestId,
    },
  });
}

interface HeaderButtonProps
  extends MarkRequired<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children" | "id" | "onClick"
  > {
  orderBy: OrderByState;
}

const HeaderButton = ({ orderBy, children, ...props }: HeaderButtonProps) => (
  <button
    className={clsx(Styles.headerButton)}
    type="button"
    {...props}
    aria-label={props.title}
  >
    {children}
    <span className={clsx(Styles.iconStack)}>
      <span
        className={clsx(
          Styles.icon,
          "hoylu-ui-icons-triangle-up",
          orderBy.field === props.id && orderBy.order === "asc" && "active",
        )}
      />
      <span
        className={clsx(
          Styles.icon,
          "hoylu-ui-icons-triangle-down",
          orderBy.field === props.id && orderBy.order === "desc" && "active",
        )}
      />
    </span>
  </button>
);

interface HeaderSearchProps
  extends Required<
    Pick<
      DetailedHTMLProps<
        InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
      >,
      "id" | "onChange" | "value"
    >
  > {
  onClear: () => void;
}

const HeaderSearch = ({ onClear, ...props }: HeaderSearchProps) => {
  const t = useI18n();

  return (
    <span className={clsx(Styles.searchContainer)}>
      <span
        className={clsx(
          Styles.searchIcons,
          Styles.search,
          "hoylu-ui-icons-magnifier",
        )}
      />
      <input
        className={clsx(Styles.searchInput)}
        placeholder={t("SEARCH")}
        {...props}
      />
      <button
        className={clsx(
          Styles.searchIcons,
          Styles.clearSearch,
          "hoylu-ui-icons-windowclose",
        )}
        onClick={onClear}
        title={t("CLEAR_SEARCH")}
      />
    </span>
  );
};
